// src/pages/AdminSeatPlanner.jsx
import { useEffect, useMemo, useRef, useState } from "react";

const API = process.env.REACT_APP_API_BASE ?? "";

/* ------------------------------ helpers / fetch ------------------------------ */
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

function withAuth(opts = {}) {
  const token = localStorage.getItem("token") || "";
  return {
    credentials: "include",
    ...opts,
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  };
}
async function fetchJsonStrict(url, opts) {
  const res = await fetch(url, withAuth(opts));
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} on ${url}${text ? ` — ${text}` : ""}`);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : null;
}
async function fetchJsonSoft(url, opts) {
  try {
    return await fetchJsonStrict(url, opts);
  } catch {
    return null;
  }
}

// normalize seat row from DB (handles lowercase/UPPERCASE)
const normSeat = (s = {}) => ({
  seat_id: s.seat_id ?? s.SEAT_ID ?? null,
  seat_row: Number(s.seat_row ?? s.SEAT_ROW ?? 0),
  seat_column: Number(s.seat_column ?? s.SEAT_COLUMN ?? 0),
  seat_type: s.seat_type ?? s.SEAT_TYPE ?? "Standard",
  row_label: s.row_label ?? s.ROW_LABEL ?? "",
  row_alignment: (s.row_alignment ?? s.ROW_ALIGNMENT ?? "left").toLowerCase(),
  label_text: s.label_text ?? s.LABEL_TEXT ?? "",
  is_label: !!(s.is_label ?? s.IS_LABEL ?? false),
  hall_name: s.hall_name ?? s.HALL_NAME ?? undefined,
});

/* color helper for types (BG/EN tolerant) */
/* replace colorForType with this */
const colorForType = (t = "") => {
  const s = String(t).toLowerCase();
  // vivid, high-contrast pills (works with BG names too)
  if (s.includes("vip"))                   return "bg-amber-400 text-black shadow";
  if (s.includes("балкон") || s.includes("balcony"))
                                           return "bg-fuchsia-600 text-white shadow";
  if (s.includes("намал") || s.includes("discount"))
                                           return "bg-teal-500 text-black shadow";
  if (s.includes("дет") || s.includes("child"))
                                           return "bg-emerald-500 text-black shadow";
  if (s.includes("пенс") || s.includes("senior"))
                                           return "bg-orange-400 text-black shadow";
  if (s.includes("станд") || s.includes("standard"))
                                           return "bg-indigo-500 text-white shadow";
  return "bg-slate-500 text-white shadow";
};

/* add this constant near helpers to style EMPTY cells consistently */
const EMPTY_CELL =
  "bg-slate-600/55 ring-1 ring-white/10 backdrop-blur-sm text-white/70";


/* -------------------------------- component -------------------------------- */
export default function AdminSeatPlanner() {
  const [cinemas, setCinemas] = useState([]);
  const [halls, setHalls] = useState([]);
  const [selectedCinema, setSelectedCinema] = useState("");
  const [selectedHall, setSelectedHall] = useState("");

  const [newHallName, setNewHallName] = useState("");

  const [seatType, setSeatType] = useState("Standard");
  const [screenWidth, setScreenWidth] = useState(420);
  const [autoScreen, setAutoScreen] = useState(true);

  const [seatSize, setSeatSize] = useState(40); // px per seat
  const [cellGap, setCellGap] = useState(8);    // px between seats

  const [seatGrid, setSeatGrid] = useState([]);      // 2D array (seat or null)
  const [rowLabels, setRowLabels] = useState([]);    // ["A","B",...]
  const [rowAlignments, setRowAlignments] = useState([]); // left|center|right
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [flash, setFlash] = useState(null);

  const workRef = useRef(null);

  // pricing
  const [pricing, setPricing] = useState([]);  // rows from /api/seat-pricing/:cinema_id
  const [basePricing, setBasePricing] = useState([]); // raw rows from /api/seat-pricing/:cinema_id/base
  const [hallPricing, setHallPricing] = useState([]); // raw rows from /api/seat-pricing/:cinema_id/hall/:hall_id
  const [pricingFlash, setPricingFlash] = useState(null);
  const [basePricingFlash, setBasePricingFlash] = useState(null);
  const [showBasePricingEditor, setShowBasePricingEditor] = useState(false);
  const distinctTypes = useMemo(() => {
    const base = ["Standard","VIP","Balcony"];
    const dyn = Array.from(new Set(pricing.map(p => p.seat_type))).filter(Boolean);
    return Array.from(new Set([...dyn, ...base]));
  }, [pricing]);

  const seatTypeSuggestions = useMemo(() => {
    const s = new Set();
    const add = (v) => {
      const t = String(v || "").trim();
      if (t) s.add(t);
    };

    // common defaults (EN + BG)
    ["Standard", "VIP", "Balcony", "Стандарт", "Балкон", "ВИП"].forEach(add);

    distinctTypes.forEach(add);
    basePricing.forEach((r) => add(r?.seat_type));
    hallPricing.forEach((r) => add(r?.seat_type));

    // seat types already used in the current plan
    for (const row of seatGrid) {
      for (const cell of row || []) {
        if (cell?.seat_type) add(cell.seat_type);
      }
    }

    return Array.from(s).sort((a, b) => String(a).localeCompare(String(b), "bg"));
  }, [distinctTypes, basePricing, hallPricing, seatGrid]);

  // grid size derived from seatGrid
  const gridSize = useMemo(
    () => ({ rows: seatGrid.length, cols: seatGrid[0]?.length || 0 }),
    [seatGrid]
  );

  // pixel width of the matrix (without the label column)
  const gridPixelWidth = useMemo(() => {
    const cols = gridSize.cols || 0;
    if (!cols) return 0;
    return cols * seatSize + Math.max(0, cols - 1) * cellGap;
  }, [gridSize.cols, seatSize, cellGap]);

  // auto-sync screen width to matrix width
  useEffect(() => {
    if (autoScreen) setScreenWidth(gridPixelWidth || 420);
  }, [gridPixelWidth, autoScreen]);

  /* ---------- load cinemas + halls (keeps your existing endpoints) ---------- */
  useEffect(() => {
    (async () => {
      const h = await fetchJsonSoft(`${API}/api/halls`);
      const hallsArr = Array.isArray(h) ? h : [];
      setHalls(hallsArr);

      const ids = [
        ...new Set(
          hallsArr.map((r) => Number(r.cinema_id ?? r.CINEMA_ID)).filter(Boolean)
        ),
      ];

      const details = await Promise.all(
        ids.map((id) => fetchJsonSoft(`${API}/api/cinemas/${id}`))
      );

      const list = ids
        .map((id, i) => {
          const r = details[i] || {};
          const name =
            r?.brief?.name ??
            r?.cinema_name ??
            r?.CINEMA_NAME ??
            r?.company_name ??
            r?.name ??
            `Кино #${id}`;
          return { cinema_id: id, cinema_name: name };
        })
        .sort((a, b) =>
          String(a.cinema_name).localeCompare(String(b.cinema_name), "bg")
        );

      setCinemas(list);
    })();
  }, []);

  // when cinema changes, preselect hall & load pricing
  useEffect(() => {
    if (!selectedCinema) {
      setSelectedHall("");
      setPricing([]);
      setHallPricing([]);
      return;
    }
    const first = halls.find(
      (h) => Number(h.cinema_id ?? h.CINEMA_ID) === Number(selectedCinema)
    );
    if (first) setSelectedHall(String(first.hall_id ?? first.HALL_ID));
    else setSelectedHall("");
  }, [selectedCinema, halls]);

  // load effective pricing + hall override list (does NOT modify cinema pricing)
  useEffect(() => {
    if (!selectedCinema) {
      setPricing([]);
      setBasePricing([]);
      setHallPricing([]);
      return;
    }

    (async () => {
      const cid = selectedCinema;
      const hid = selectedHall;

      const pr = await fetchJsonSoft(
        `${API}/api/seat-pricing/${cid}${hid ? `?hall_id=${hid}` : ""}`
      );
      setPricing(Array.isArray(pr) ? pr : []);

      const br = await fetchJsonSoft(`${API}/api/seat-pricing/${cid}/base`);
      setBasePricing(Array.isArray(br) ? br : []);

      if (hid) {
        const hr = await fetchJsonSoft(`${API}/api/seat-pricing/${cid}/hall/${hid}`);
        setHallPricing(Array.isArray(hr) ? hr : []);
      } else {
        setHallPricing([]);
      }
    })();
  }, [selectedCinema, selectedHall]);


  /* ------------------------------ load hall layout ------------------------------ */
  useEffect(() => {
    if (!selectedHall) {
      initDefaultGrid();
      return;
    }
    (async () => {
      setLoading(true);
      const rows = await fetchJsonSoft(`${API}/api/seats/hall/${selectedHall}`);
      if (!Array.isArray(rows) || rows.length === 0) {
        initDefaultGrid();
        setLoading(false);
        return;
      }
      const data = rows.map(normSeat);

      const maxRow = Math.max(...data.map((s) => s.seat_row || 0));
      const maxCol = Math.max(...data.map((s) => s.seat_column || 0));
      const grid = Array.from({ length: maxRow }, (_, r) =>
        Array.from({ length: maxCol }, (_, c) => {
          const found = data.find(
            (s) => s.seat_row === r + 1 && s.seat_column === c + 1
          );
          return found ? { ...found } : null;
        })
      );
      const labels = Array.from({ length: maxRow }, (_, i) => {
        const any = data.some((s) => s.seat_row === i + 1);
        return any
          ? data.find((s) => s.seat_row === i + 1)?.row_label || ""
          : "";
      });
      const aligns = Array.from({ length: maxRow }, (_, i) =>
        (data.find((s) => s.seat_row === i + 1)?.row_alignment || "left").toLowerCase()
      );

      setSeatGrid(grid);
      setRowLabels(labels);
      setRowAlignments(aligns);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHall]);

  /* --------------------------------- grid ops --------------------------------- */
  const initDefaultGrid = () => {
    const rows = 6, cols = 12;
    setSeatGrid(Array.from({ length: rows }, () => Array.from({ length: cols }, () => null)));
    setRowLabels(Array.from({ length: rows }, (_, i) => String.fromCharCode(65 + i)));
    setRowAlignments(Array.from({ length: rows }, () => "left"));
  };

  const addRow = () => {
    const cols = gridSize.cols || 8;
    setSeatGrid((p) => [...p, Array.from({ length: cols }, () => null)]);
    setRowLabels((p) => [...p, ""]);
    setRowAlignments((p) => [...p, "left"]);
  };
  const removeRow = () => {
    if (gridSize.rows <= 1) return;
    setSeatGrid((p) => p.slice(0, -1));
    setRowLabels((p) => p.slice(0, -1));
    setRowAlignments((p) => p.slice(0, -1));
  };
  const addCol = () => setSeatGrid((p) => p.map((r) => [...r, null]));
  const removeCol = () => {
    if (gridSize.cols <= 1) return;
    setSeatGrid((p) => p.map((r) => r.slice(0, -1)));
  };
  const insertSpacerRowAfterFirst = () => {
    if (!seatGrid.length) return;
    const empty = Array.from({ length: gridSize.cols || 0 }, () => null);
    setSeatGrid((p) => [p[0], empty, ...p.slice(1)]);
    setRowLabels((p) => [p[0] || "", "", ...p.slice(1)]);
    setRowAlignments((p) => [p[0] || "left", "left", ...p.slice(1)]);
  };
  const shiftLeft  = () => setSeatGrid((p) => p.map((row) => row.slice(1).concat(null)));
  const shiftRight = () => setSeatGrid((p) => p.map((row) => [null, ...row.slice(0, -1)]));

  // paint seats
  const toggleSeat = (r, c) => {
    setSeatGrid((prev) => {
      const grid = prev.map((row) => row.slice());
      const cell = grid[r][c];
      if (cell) {
        grid[r][c] = null;
      } else {
        grid[r][c] = {
          seat_row: r + 1,
          seat_column: c + 1,
          seat_type: seatType,
          row_label: rowLabels[r] || "",
          label_text: "",
          is_label: false,
          row_alignment: rowAlignments[r] || "left",
        };
      }
      return grid;
    });
  };
  const onMouseDown = (r, c) => { setIsDragging(true); toggleSeat(r, c); };
  const onMouseEnter = (r, c) => { if (isDragging) toggleSeat(r, c); };
  const onMouseUp = () => setIsDragging(false);

  const updateRowLabel = (i, v) =>
    setRowLabels((prev) => prev.map((x, idx) => (idx === i ? v : x)));
  const updateRowAlignment = (i, v) =>
    setRowAlignments((prev) => prev.map((x, idx) => (idx === i ? v : x)));
  const updateSeatLabel = (r, c, v) => {
    setSeatGrid((prev) => {
      const grid = prev.map((row) => row.slice());
      if (grid[r][c]) grid[r][c] = { ...grid[r][c], label_text: v };
      return grid;
    });
  };

  /* -------------------------------- persist --------------------------------- */
  const handleSave = async () => {
    if (!selectedHall) {
      setFlash({ type: "error", msg: "Изберете зала" });
      setTimeout(() => setFlash(null), 2000);
      return;
    }
    const flattened = seatGrid
      .flatMap((row, rIdx) =>
        row.map((cell, cIdx) =>
          cell
            ? {
                ...cell,
                seat_row: rIdx + 1,
                seat_column: cIdx + 1,
                row_label: rowLabels[rIdx] || null,
                row_alignment: rowAlignments[rIdx] || "left",
                is_label: !!cell.is_label,
              }
            : null
        )
      )
      .filter(Boolean);

    try {
      await fetchJsonStrict(`${API}/api/seats/hall/${selectedHall}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(flattened),
      });
      setFlash({ type: "ok", msg: "✅ Съхранено!" });
    } catch {
      setFlash({ type: "error", msg: "Грешка при запис." });
    }
    setTimeout(() => setFlash(null), 2500);
  };

  const createHall = async () => {
    const cid = Number(selectedCinema);
    if (!cid || !newHallName.trim()) {
      setFlash({ type: "error", msg: "Изберете кино и въведете име на зала." });
      setTimeout(() => setFlash(null), 2500);
      return;
    }
    try {
      await fetchJsonStrict(`${API}/api/halls`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cinema_id: cid, hall_name: newHallName.trim() }),
      });

      const all = await fetchJsonSoft(`${API}/api/halls`);
      setHalls(Array.isArray(all) ? all : []);
      setNewHallName("");
      setFlash({ type: "ok", msg: "✅ Залата е създадена." });
    } catch {
      setFlash({ type: "error", msg: "Неуспешно създаване на зала." });
    }
    setTimeout(() => setFlash(null), 2500);
  };


  /* -------------------------- hall pricing editor -------------------------- */
  const reloadPricing = async () => {
    if (!selectedCinema) return;

    const cid = selectedCinema;
    const hid = selectedHall;

    const pr = await fetchJsonSoft(
      `${API}/api/seat-pricing/${cid}${hid ? `?hall_id=${hid}` : ""}`
    );
    setPricing(Array.isArray(pr) ? pr : []);

    const br = await fetchJsonSoft(`${API}/api/seat-pricing/${cid}/base`);
    setBasePricing(Array.isArray(br) ? br : []);

    if (hid) {
      const hr = await fetchJsonSoft(`${API}/api/seat-pricing/${cid}/hall/${hid}`);
      setHallPricing(Array.isArray(hr) ? hr : []);
    } else {
      setHallPricing([]);
    }
  };

  /* -------------------------- cinema base pricing editor -------------------------- */
  const updateBasePricingLocal = (key, field, value) => {
    setBasePricing((prev) =>
      prev.map((r) =>
        String(r.id ?? r._tmpId) === String(key) ? { ...r, [field]: value } : r
      )
    );
  };

  const addBasePricingRow = (seatTypePreset = null) => {
    if (!selectedCinema) return;
    const tmp = `${Date.now()}-${Math.random()}`;
    setBasePricing((p) => [
      ...p,
      {
        _tmpId: tmp,
        seat_type: seatTypePreset ?? seatType ?? "Standard",
        category: "normal",
        price: "",
      },
    ]);
    setShowBasePricingEditor(true);
  };

  const saveBasePricingRow = async (row) => {
    if (!selectedCinema) return;
    const payload = {
      seat_type: String(row.seat_type || "").trim(),
      category: String(row.category || "").trim(),
      price: row.price,
    };

    try {
      if (!payload.seat_type || !payload.category || payload.price === "") {
        setBasePricingFlash("⚠️ seat_type, category и price са задължителни.");
        setTimeout(() => setBasePricingFlash(null), 2500);
        return;
      }

      const urlBase = `${API}/api/seat-pricing/${selectedCinema}/base`;

      if (row.id) {
        await fetchJsonStrict(`${urlBase}/${row.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await fetchJsonStrict(urlBase, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      setBasePricingFlash("✅ Запазено.");
      await reloadPricing();
    } catch {
      setBasePricingFlash("❌ Грешка при запис на цени.");
    } finally {
      setTimeout(() => setBasePricingFlash(null), 2500);
    }
  };

  const deleteBasePricingRow = async (row) => {
    if (!selectedCinema) return;
    try {
      const urlBase = `${API}/api/seat-pricing/${selectedCinema}/base`;

      if (row.id) {
        await fetchJsonStrict(`${urlBase}/${row.id}`, { method: "DELETE" });
      }

      setBasePricing((p) => p.filter((x) => String(x.id ?? x._tmpId) !== String(row.id ?? row._tmpId)));
      setBasePricingFlash("🗑️ Изтрито.");
      await reloadPricing();
    } catch {
      setBasePricingFlash("❌ Грешка при изтриване.");
    } finally {
      setTimeout(() => setBasePricingFlash(null), 2500);
    }
  };

  const updateHallPricingLocal = (key, field, value) => {
    setHallPricing((prev) =>
      prev.map((r) =>
        String(r.id ?? r._tmpId) === String(key) ? { ...r, [field]: value } : r
      )
    );
  };

  const addHallPricingRow = () => {
    if (!selectedHall) return;
    const tmp = `${Date.now()}-${Math.random()}`;
    setHallPricing((p) => [
      ...p,
      { _tmpId: tmp, seat_type: seatType || "Standard", category: "normal", price: "" },
    ]);
  };

  const saveHallPricingRow = async (row) => {
    if (!selectedCinema || !selectedHall) return;

    const payload = {
      seat_type: String(row.seat_type || "").trim(),
      category: String(row.category || "").trim(),
      price: row.price,
    };

    try {
      if (!payload.seat_type || !payload.category || payload.price === "") {
        setPricingFlash("⚠️ seat_type, category и price са задължителни.");
        setTimeout(() => setPricingFlash(null), 2500);
        return;
      }

      const urlBase = `${API}/api/seat-pricing/${selectedCinema}/hall/${selectedHall}`;

      if (row.id) {
        await fetchJsonStrict(`${urlBase}/${row.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await fetchJsonStrict(urlBase, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      setPricingFlash("✅ Запазено.");
      await reloadPricing();
    } catch (e) {
      setPricingFlash("❌ Грешка при запис на цени (провери migration за seat_pricing_hall).");
    } finally {
      setTimeout(() => setPricingFlash(null), 2500);
    }
  };

  const deleteHallPricingRow = async (row) => {
    if (!selectedCinema || !selectedHall) return;

    try {
      const urlBase = `${API}/api/seat-pricing/${selectedCinema}/hall/${selectedHall}`;

      if (row.id) {
        await fetchJsonStrict(`${urlBase}/${row.id}`, { method: "DELETE" });
      }

      // remove locally even if it was a new unsaved row
      setHallPricing((p) => p.filter((x) => String(x.id ?? x._tmpId) !== String(row.id ?? row._tmpId)));

      setPricingFlash("🗑️ Изтрито.");
      await reloadPricing();
    } catch {
      setPricingFlash("❌ Грешка при изтриване.");
    } finally {
      setTimeout(() => setPricingFlash(null), 2500);
    }
  };

  const copyCinemaPricingToHall = async () => {
    if (!selectedCinema || !selectedHall) return;
    try {
      await fetchJsonStrict(
        `${API}/api/seat-pricing/${selectedCinema}/hall/${selectedHall}/copy-from-cinema`,
        { method: "POST" }
      );
      setPricingFlash("📋 Копирано от киното към залата.");
      await reloadPricing();
    } catch {
      setPricingFlash("❌ Неуспешно копиране (провери migration за seat_pricing_hall).");
    } finally {
      setTimeout(() => setPricingFlash(null), 2500);
    }
  };

  /* ------------- derived: pricing grouped by seat_type (for legend) ---------- */
  const pricingByType = useMemo(() => {
    const map = new Map();
    for (const r of pricing) {
      const key = r.seat_type || "—";
      const arr = map.get(key) || [];
      arr.push(r);
      map.set(key, arr);
    }
    // order categories a bit
    for (const [k, arr] of map) {
      arr.sort((a, b) => {
        const order = { normal: 0, child: 1, senior: 2, discount: 3 };
        return (order[a.category] ?? 9) - (order[b.category] ?? 9);
      });
      map.set(k, arr);
    }
    return map;
  }, [pricing]);


  /* ---------------------------------- UI ---------------------------------- */
  return (
    <div className="min-h-screen relative p-4 md:p-6 [color-scheme:light]" onMouseUp={onMouseUp}>
      {/* background like dashboard */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <img
          src="https://latonacinema.com/wp-content/uploads/2025/06/cinema-hall-with-rows-of-grey-seats-in-darkness.jpg"
          alt="фон"
          className="h-full w-full object-cover blur-[2px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2c204f]/80 via-black/60 to-[#6c56b5]/70" />
      </div>

      {/* card */}
      <div className="max-w-screen-2xl mx-auto bg-white/8 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-4 md:p-6 text-white">
        {/* header row */}
        <div className="flex items-center gap-3 mb-4">
          <img
            src="https://latonacinema.com/wp-content/uploads/2025/06/cropped-latonacinema.png"
            alt="logo"
            className="h-10"
          />
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">🪑 План на залите</h1>
        </div>

        {/* suggestions for seat_type inputs */}
        <datalist id="seatTypeOptions">
          {seatTypeSuggestions.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>

        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <select
            onChange={(e) => setSelectedCinema(e.target.value)}
            value={selectedCinema}
            className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            <option value="">🎥 Изберете кино</option>
            {cinemas.map((c) => (
              <option key={c.cinema_id} value={c.cinema_id}>
                {c.cinema_name}
              </option>
            ))}
          </select>

          <select
            onChange={(e) => setSelectedHall(e.target.value)}
            value={selectedHall}
            className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            <option value="">🏢 Изберете зала</option>
            {halls
              .filter((h) => Number(h.cinema_id ?? h.CINEMA_ID) === Number(selectedCinema))
              .map((h) => (
                <option key={h.hall_id ?? h.HALL_ID} value={h.hall_id ?? h.HALL_ID}>
                  {h.hall_name ?? h.HALL_NAME}
                </option>
              ))}
          </select>

          <input
            type="text"
            placeholder="Нова зала (име)"
            value={newHallName}
            onChange={(e) => setNewHallName(e.target.value)}
            className="px-3 py-2 bg-white/90 border border-white/40 text-slate-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400"
          />
          <button
            onClick={createHall}
            className="px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white border border-white/10"
          >
            ✨ Създай зала
          </button>

          {/* seat type brush — built from pricing types (fallback to defaults) */}
          <div className="flex items-center gap-2 ml-2 flex-wrap">
            {distinctTypes.map((t) => (
              <button
                key={t}
                onClick={() => setSeatType(t)}
                className={`px-2.5 py-1.5 rounded-lg text-sm border border-white/20 hover:opacity-95 transition
                            ${colorForType(t)} ${seatType === t ? "ring-2 ring-white/80" : ""}`}
                title={`Инструмент: ${t}`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* size + fit */}
          <div className="flex items-center gap-3 ml-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/80">Seat</span>
              <input
                type="range"
                min={24}
                max={64}
                value={seatSize}
                onChange={(e) => setSeatSize(Number(e.target.value) || 40)}
                className="w-36"
              />
              <span className="text-sm text-white/70 w-10 text-right">{seatSize}px</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/80">Gap</span>
              <input
                type="range"
                min={0}
                max={18}
                value={cellGap}
                onChange={(e) => setCellGap(Number(e.target.value) || 8)}
                className="w-28"
              />
            </div>
            <button
              onClick={() => {
                const wrap = workRef.current;
                if (!wrap) return;
                const avail = wrap.clientWidth - 160 - 48; // labels + padding
                const cols = gridSize.cols || 1;
                const size = Math.floor((avail + cellGap) / cols - cellGap);
                setSeatSize(clamp(size, 24, 64));
              }}
              className="px-3 py-2 rounded-xl bg-white/10 border border-white/20 hover:bg-white/15"
            >
              ↔️ Fit width
            </button>
          </div>

          {/* rows/cols & spacer/shift */}
          <div className="flex items-center gap-2 ml-2">
            <button onClick={addRow} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15">➕ Ред</button>
            <button onClick={removeRow} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15">➖ Ред</button>
            <button onClick={addCol} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15">➕ Колона</button>
            <button onClick={removeCol} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15">➖ Колона</button>
            <button onClick={insertSpacerRowAfterFirst} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15">↔️ Spacer</button>
            <button onClick={shiftLeft} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15">⬅️ Shift</button>
            <button onClick={shiftRight} className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15">➡️ Shift</button>
          </div>

          {/* right side: screen + save */}
          <div className="ml-auto flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoScreen}
                onChange={(e) => setAutoScreen(e.target.checked)}
              />
              Auto screen
            </label>
            {!autoScreen && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/80">Ширина екран</span>
                <input
                  type="range"
                  min={220}
                  max={1200}
                  value={screenWidth}
                  onChange={(e) => setScreenWidth(clamp(Number(e.target.value) || 420, 220, 1200))}
                  className="w-40"
                />
                <span className="text-sm text-white/70 w-14 text-right">{screenWidth}px</span>
              </div>
            )}
            <button
              onClick={handleSave}
              className="px-4 py-2 rounded-xl bg-gradient-to-br from-[#6c56b5] to-[#2c204f] text-white border border-white/20 hover:opacity-95"
            >
              💾 Запази
            </button>
          </div>
        </div>

        {flash && (
          <div className={`mb-3 text-sm ${flash.type === "ok" ? "text-emerald-300" : "text-rose-300"}`}>
            {flash.msg}
          </div>
        )}

        {/* Cinema base pricing editor (affects halls without overrides) */}
        {selectedCinema && (
          <div className="mb-4 rounded-xl border border-white/15 bg-white/5 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowBasePricingEditor((v) => !v)}
                className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15 text-sm"
              >
                {showBasePricingEditor ? "🔽" : "▶️"} Цени на кино (base)
              </button>

              <div className="text-xs text-white/70">
                Внимание: това влияе на всички зали в киното, които НЯМАТ override.
              </div>

              <div className="ml-auto flex flex-wrap items-center gap-2">
                <button
                  onClick={() => addBasePricingRow(null)}
                  className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15 text-sm"
                >
                  ➕ Добави ред
                </button>
                <button
                  onClick={() => addBasePricingRow("Standard")}
                  className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15 text-sm"
                >
                  + Standard
                </button>
                <button
                  onClick={() => addBasePricingRow("Balcony")}
                  className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15 text-sm"
                >
                  + Balcony
                </button>
                <button
                  onClick={() => addBasePricingRow("VIP")}
                  className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15 text-sm"
                >
                  + VIP
                </button>
              </div>
            </div>

            {showBasePricingEditor && (
              <>
                {basePricingFlash && <div className="mt-2 text-xs text-white/80">{basePricingFlash}</div>}

                <div className="mt-3 overflow-auto">
                  <table className="min-w-[760px] w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-white/80">
                        <th className="py-2 pr-3">Seat type</th>
                        <th className="py-2 pr-3">Category</th>
                        <th className="py-2 pr-3">Price</th>
                        <th className="py-2 pr-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {basePricing.length === 0 ? (
                        <tr>
                          <td className="py-3 text-white/70" colSpan={4}>
                            Няма цени за това кино.
                          </td>
                        </tr>
                      ) : (
                        basePricing.map((r) => {
                          const key = r.id ?? r._tmpId;
                          return (
                            <tr key={key} className="border-t border-white/10">
                              <td className="py-2 pr-3">
                                <input
                                  list="seatTypeOptions"
                                  value={r.seat_type ?? ""}
                                  onChange={(e) => updateBasePricingLocal(key, "seat_type", e.target.value)}
                                  className="w-full px-2 py-1 rounded-lg bg-white/90 border border-white/40 text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                />
                              </td>
                              <td className="py-2 pr-3">
                                <select
                                  value={r.category ?? "normal"}
                                  onChange={(e) => updateBasePricingLocal(key, "category", e.target.value)}
                                  className="w-full px-2 py-1 rounded-lg bg-white/90 border border-white/40 text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                >
                                  <option value="normal">normal</option>
                                  <option value="child">child</option>
                                  <option value="senior">senior</option>
                                  <option value="discount">discount</option>
                                </select>
                              </td>
                              <td className="py-2 pr-3">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={r.price ?? ""}
                                  onChange={(e) => updateBasePricingLocal(key, "price", e.target.value)}
                                  className="w-full px-2 py-1 rounded-lg bg-white/90 border border-white/40 text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                />
                              </td>
                              <td className="py-2 pr-3">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => saveBasePricingRow(r)}
                                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                                  >
                                    💾 Save
                                  </button>
                                  <button
                                    onClick={() => deleteBasePricingRow(r)}
                                    className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm"
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Hall pricing editor (safe: overrides only the selected hall) */}
        {selectedCinema && selectedHall && (
          <div className="mb-4 rounded-xl border border-white/15 bg-white/5 p-3">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <div className="text-sm font-semibold">🛠️ Цени за тази зала (override)</div>

              <button
                onClick={addHallPricingRow}
                className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15 text-sm"
              >
                ➕ Добави ред
              </button>

              <button
                onClick={copyCinemaPricingToHall}
                className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15 text-sm"
              >
                📋 Копирай от киното
              </button>

              <div className="text-xs text-white/70">
                Ако таблицата е празна, системата ползва ценоразписа на киното.
              </div>
            </div>

            {pricingFlash && <div className="mb-2 text-xs text-white/80">{pricingFlash}</div>}

            <div className="overflow-auto">
              <table className="min-w-[760px] w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left text-white/80">
                    <th className="py-2 pr-3">Seat type</th>
                    <th className="py-2 pr-3">Category</th>
                    <th className="py-2 pr-3">Price</th>
                    <th className="py-2 pr-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hallPricing.length === 0 ? (
                    <tr>
                      <td className="py-3 text-white/70" colSpan={4}>
                        Няма override цени за тази зала.
                      </td>
                    </tr>
                  ) : (
                    hallPricing.map((r) => {
                      const key = r.id ?? r._tmpId;
                      return (
                        <tr key={key} className="border-t border-white/10">
                          <td className="py-2 pr-3">
                            <input
                              list="seatTypeOptions"
                              value={r.seat_type ?? ""}
                              onChange={(e) => updateHallPricingLocal(key, "seat_type", e.target.value)}
                              className="w-full px-2 py-1 rounded-lg bg-white/90 border border-white/40 text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                          </td>
                          <td className="py-2 pr-3">
                            <select
                              value={r.category ?? "normal"}
                              onChange={(e) => updateHallPricingLocal(key, "category", e.target.value)}
                              className="w-full px-2 py-1 rounded-lg bg-white/90 border border-white/40 text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
                            >
                              <option value="normal">normal</option>
                              <option value="child">child</option>
                              <option value="senior">senior</option>
                              <option value="discount">discount</option>
                            </select>
                          </td>
                          <td className="py-2 pr-3">
                            <input
                              type="number"
                              step="0.01"
                              value={r.price ?? ""}
                              onChange={(e) => updateHallPricingLocal(key, "price", e.target.value)}
                              className="w-full px-2 py-1 rounded-lg bg-white/90 border border-white/40 text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => saveHallPricingRow(r)}
                                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                              >
                                💾 Save
                              </button>
                              <button
                                onClick={() => deleteHallPricingRow(r)}
                                className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pricing legend */}
        {selectedCinema && pricing.length > 0 && (
          <div className="mb-4 rounded-xl border border-white/15 bg-white/5 p-3">
            <div className="text-sm font-semibold mb-2">💰 Ценоразпис (по тип седалка)</div>
            <div className="flex flex-wrap gap-2">
              {Array.from(pricingByType.entries()).map(([type, rows]) => (
                <div key={type} className="rounded-lg border border-white/15 bg-white/5 p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-block w-3 h-3 rounded ${colorForType(type)}`} />
                    <span className="text-sm font-medium">{type}</span>
                  </div>
                  <div className="text-xs text-white/80 space-y-0.5">
                    {rows.map((r) => (
                      <div key={`${type}-${r.category}`}>
                        <span className="opacity-80">{r.category}</span>:{" "}
                        <b>{Number(r.price).toFixed(2)}</b> лв.
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Work area fills width */}
        <div ref={workRef} className="overflow-auto w-full">
  <div className="rounded-2xl border border-white/15 bg-white/5 p-4 w-full">
    {/* center container */}
    <div className="max-w-full flex flex-col items-center justify-start">
      <div
        className="text-center text-sm font-semibold mb-4 rounded-lg py-1 px-4
                   border border-white/20 bg-gradient-to-r from-white/10 to-white/5"
        style={{ width: `${screenWidth || 420}px` }}
      >
        SCREEN
      </div>

      {loading ? (
        <div className="p-10 text-center text-white/70">Зареждане…</div>
      ) : (
        seatGrid.map((row, r) => {
          const align = rowAlignments[r] || "left";
          const just =
            align === "center"
              ? "justify-center"
              : align === "right"
              ? "justify-end"
              : "justify-start";

          return (
            <div key={r}
                 className="flex gap-3 mb-2 items-center w-full"
                 style={{ maxWidth: gridPixelWidth + 200 /* label column + margin */ }}>
              {/* label + alignment selector */}
              <div className="flex items-center gap-2 min-w-[160px]">
                <input
                  className="w-24 text-xs bg-white/90 border border-white/40 text-slate-900 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  value={rowLabels[r] || ""}
                  onChange={(e) => updateRowLabel(r, e.target.value)}
                  placeholder={`Ред ${r + 1}`}
                />
                <select
                  className="text-xs bg-white/90 border border-white/40 text-slate-900 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  value={rowAlignments[r]}
                  onChange={(e) => updateRowAlignment(r, e.target.value)}
                >
                  <option value="left">⬅️ Left</option>
                  <option value="center">↔️ Center</option>
                  <option value="right">➡️ Right</option>
                </select>
              </div>

              {/* the actual seats row, centered within the available width */}
              <div className={`flex flex-1 ${just}`}
                   style={{ gap: `${cellGap}px` }}>
                {row.map((cell, c) => {
                  const base = cell ? colorForType(cell.seat_type) : EMPTY_CELL;
                  return (
                    <div
                      key={c}
                      className={`rounded-lg cursor-pointer flex items-center justify-center text-[11px] font-medium
                                  ${base} hover:brightness-110 transition shadow-sm`}
                      style={{ width: seatSize, height: seatSize, borderRadius: 10 }}
                      onMouseDown={() => onMouseDown(r, c)}
                      onMouseEnter={() => onMouseEnter(r, c)}
                      title={cell ? `${cell.seat_type}${cell.is_label ? " • label" : ""}` : "empty"}
                    >
                      {cell && (
                        <input
                          value={cell.label_text || ""}
                          onChange={(e) => updateSeatLabel(r, c, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="bg-transparent w-full text-center focus:outline-none placeholder-white/70"
                          style={{ color: "currentColor" }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  </div>
</div>

      </div>
    </div>
  );
}
